import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

interface PhotoBoothProps {
  onStatsUpdate: (stats: { photos: number }) => void;
}

interface Filter {
  id: string;
  name: string;
  emoji: string;
  style: string;
  overlay?: {
    type: 'emoji' | 'shape';
    content: string;
    position: { x: number; y: number; width: number; height: number };
    rotation?: number;
  }[];
}

const VirtualPhotoBooth: React.FC<PhotoBoothProps> = ({ onStatsUpdate }) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('none');
  const [capturedPhotos, setCapturedPhotos] = useState<string[]>([]);
  const [showGallery, setShowGallery] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const filters: Filter[] = React.useMemo(() => [
    { id: 'none', name: 'No Filter', emoji: '📷', style: '' },
    { id: 'vintage', name: 'Vintage', emoji: '📸', style: 'sepia(1) contrast(1.2) saturate(0.8)' },
    { id: 'blackwhite', name: 'Black & White', emoji: '⚫', style: 'grayscale(1) contrast(1.1)' },
    { id: 'warm', name: 'Warm', emoji: '🌅', style: 'sepia(0.5) saturate(1.2) hue-rotate(20deg)' },
    { id: 'cool', name: 'Cool', emoji: '❄️', style: 'hue-rotate(200deg) saturate(1.1) brightness(1.1)' },
    { id: 'dramatic', name: 'Dramatic', emoji: '🎭', style: 'contrast(1.5) saturate(1.3) brightness(0.9)' },
    { id: 'romantic', name: 'Romantic', emoji: '💕', style: 'sepia(0.3) saturate(1.4) hue-rotate(320deg) brightness(1.1)' },
    { id: 'fun', name: 'Fun', emoji: '🌈', style: 'saturate(1.5) hue-rotate(60deg) brightness(1.2)' },
    // Snapchat-style overlay filters
    {
      id: 'heart-eyes',
      name: 'Heart Eyes',
      emoji: '😍',
      style: '',
      overlay: [
        { type: 'emoji', content: '💕', position: { x: 0.25, y: 0.35, width: 0.15, height: 0.15 } },
        { type: 'emoji', content: '💕', position: { x: 0.65, y: 0.35, width: 0.15, height: 0.15 } }
      ]
    },
    {
      id: 'crown',
      name: 'Crown',
      emoji: '👑',
      style: '',
      overlay: [
        { type: 'emoji', content: '👑', position: { x: 0.35, y: 0.15, width: 0.3, height: 0.25 } }
      ]
    },
    {
      id: 'sunglasses',
      name: 'Sunglasses',
      emoji: '🕶️',
      style: '',
      overlay: [
        { type: 'emoji', content: '🕶️', position: { x: 0.25, y: 0.32, width: 0.5, height: 0.2 } }
      ]
    },
    {
      id: 'dog-ears',
      name: 'Dog Ears',
      emoji: '🐶',
      style: '',
      overlay: [
        { type: 'emoji', content: '🐕', position: { x: 0.15, y: 0.1, width: 0.2, height: 0.25 }, rotation: -20 },
        { type: 'emoji', content: '🐕', position: { x: 0.65, y: 0.1, width: 0.2, height: 0.25 }, rotation: 20 }
      ]
    },
    {
      id: 'flower-crown',
      name: 'Flower Crown',
      emoji: '🌺',
      style: '',
      overlay: [
        { type: 'emoji', content: '🌺', position: { x: 0.2, y: 0.15, width: 0.15, height: 0.15 } },
        { type: 'emoji', content: '🌻', position: { x: 0.4, y: 0.12, width: 0.2, height: 0.2 } },
        { type: 'emoji', content: '🌺', position: { x: 0.65, y: 0.15, width: 0.15, height: 0.15 } }
      ]
    },
    {
      id: 'party-hat',
      name: 'Party Hat',
      emoji: '🎉',
      style: '',
      overlay: [
        { type: 'emoji', content: '🎩', position: { x: 0.35, y: 0.12, width: 0.3, height: 0.25 } }
      ]
    },
    {
      id: 'mustache',
      name: 'Mustache',
      emoji: '👨',
      style: '',
      overlay: [
        { type: 'emoji', content: '👨', position: { x: 0.35, y: 0.5, width: 0.3, height: 0.15 } }
      ]
    },
    {
      id: 'love-glasses',
      name: 'Love Glasses',
      emoji: '❤️',
      style: '',
      overlay: [
        { type: 'emoji', content: '❤️', position: { x: 0.28, y: 0.33, width: 0.18, height: 0.15 } },
        { type: 'emoji', content: '❤️', position: { x: 0.54, y: 0.33, width: 0.18, height: 0.15 } }
      ]
    },
    {
      id: 'sparkles',
      name: 'Sparkles',
      emoji: '✨',
      style: '',
      overlay: [
        { type: 'emoji', content: '✨', position: { x: 0.15, y: 0.2, width: 0.1, height: 0.1 } },
        { type: 'emoji', content: '✨', position: { x: 0.5, y: 0.15, width: 0.15, height: 0.15 } },
        { type: 'emoji', content: '✨', position: { x: 0.75, y: 0.2, width: 0.1, height: 0.1 } },
        { type: 'emoji', content: '✨', position: { x: 0.2, y: 0.5, width: 0.1, height: 0.1 } },
        { type: 'emoji', content: '✨', position: { x: 0.8, y: 0.5, width: 0.1, height: 0.1 } }
      ]
    },
    {
      id: 'hearts-around',
      name: 'Hearts Around',
      emoji: '💝',
      style: '',
      overlay: [
        { type: 'emoji', content: '💖', position: { x: 0.1, y: 0.25, width: 0.12, height: 0.12 } },
        { type: 'emoji', content: '💗', position: { x: 0.85, y: 0.25, width: 0.12, height: 0.12 } },
        { type: 'emoji', content: '💓', position: { x: 0.1, y: 0.6, width: 0.12, height: 0.12 } },
        { type: 'emoji', content: '💘', position: { x: 0.85, y: 0.6, width: 0.12, height: 0.12 } }
      ]
    }
  ], []);

  const startCamera = useCallback(async () => {
    try {
      setIsLoading(true);
      setCameraError(null);
      
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this device');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        // Wait for video to load
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play().catch(console.error);
            setIsStreaming(true);
            setIsLoading(false);
          }
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setIsLoading(false);
      let errorMessage = 'Unable to access camera. ';
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage += 'Please allow camera permissions and try again.';
        } else if (error.name === 'NotFoundError') {
          errorMessage += 'No camera found on this device.';
        } else if (error.name === 'NotSupportedError') {
          errorMessage += 'Camera not supported on this device.';
        } else {
          errorMessage += error.message;
        }
      }
      
      setCameraError(errorMessage);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas not available');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Canvas context not available');
      return;
    }

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Apply filter if selected
    const filter = filters.find(f => f.id === selectedFilter);
    if (filter && filter.style) {
      ctx.filter = filter.style;
    }

    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Draw overlay elements (emoji filters) if filter has overlays
    if (filter && filter.overlay && filter.overlay.length > 0) {
      ctx.filter = 'none'; // Reset filter for overlays
      filter.overlay.forEach(overlayItem => {
        if (overlayItem.type === 'emoji') {
          const x = overlayItem.position.x * canvas.width;
          const y = overlayItem.position.y * canvas.height;
          const width = overlayItem.position.width * canvas.width;
          const height = overlayItem.position.height * canvas.height;
          
          ctx.save();
          
          // Apply rotation if specified
          if (overlayItem.rotation) {
            const centerX = x + width / 2;
            const centerY = y + height / 2;
            ctx.translate(centerX, centerY);
            ctx.rotate((overlayItem.rotation * Math.PI) / 180);
            ctx.translate(-centerX, -centerY);
          }
          
          // Draw emoji with larger font size
          ctx.font = `${Math.max(width, height) * 1.2}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(
            overlayItem.content,
            x + width / 2,
            y + height / 2
          );
          
          ctx.restore();
        }
      });
    }

    // Add filter name overlay
    if (filter && filter.id !== 'none') {
      ctx.filter = 'none'; // Reset filter for text
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(10, canvas.height - 40, 200, 30);
      ctx.fillStyle = 'white';
      ctx.font = '16px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(`${filter.emoji} ${filter.name}`, 15, canvas.height - 20);
    }

    // Add wedding watermark
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(canvas.width - 150, 10, 140, 30);
    ctx.fillStyle = 'white';
    ctx.font = '14px Arial';
    ctx.fillText('💕 Noor & Priya 2026', canvas.width - 145, 30);

    // Convert to data URL
    const dataURL = canvas.toDataURL('image/jpeg', 0.8);
    
    // Save to state and localStorage
    const newPhotos = [...capturedPhotos, dataURL];
    setCapturedPhotos(newPhotos);
    localStorage.setItem('photoBoothPhotos', JSON.stringify(newPhotos));
    onStatsUpdate({ photos: newPhotos.length });

    // Show preview immediately
    setPreviewPhoto(dataURL);
    setShowPreview(true);

    // Show success notification
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);

    // Show success animation
    const captureButton = document.getElementById('capture-btn');
    if (captureButton) {
      captureButton.style.transform = 'scale(0.9)';
      setTimeout(() => {
        captureButton.style.transform = 'scale(1)';
      }, 150);
    }

    // Show success message
    console.log('Photo captured successfully!');
  }, [capturedPhotos, selectedFilter, filters, onStatsUpdate]);

  const downloadPhoto = (dataURL: string, index: number) => {
    const link = document.createElement('a');
    link.download = `noor-priya-photobooth-${index + 1}.jpg`;
    link.href = dataURL;
    link.click();
  };

  const showPhotoPreview = (dataURL: string) => {
    setPreviewPhoto(dataURL);
    setShowPreview(true);
  };

  const downloadPreviewPhoto = () => {
    if (previewPhoto) {
      const link = document.createElement('a');
      link.download = `noor-priya-photobooth-${Date.now()}.jpg`;
      link.href = previewPhoto;
      link.click();
    }
  };

  const clearAllPhotos = () => {
    setCapturedPhotos([]);
    localStorage.removeItem('photoBoothPhotos');
    onStatsUpdate({ photos: 0 });
  };

  // Load saved photos on mount
  React.useEffect(() => {
    const savedPhotos = localStorage.getItem('photoBoothPhotos');
    if (savedPhotos) {
      setCapturedPhotos(JSON.parse(savedPhotos));
    }
  }, []);

  // Cleanup camera on unmount
  React.useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-xl">
        <h3 className="text-2xl font-bold text-slate-800 mb-2">Virtual Photo Booth</h3>
        <p className="text-slate-600 mb-4">
          Take fun photos with virtual filters! Perfect for creating memories with Noor & Priya's wedding theme.
        </p>
        <div className="flex items-center space-x-4 text-sm">
          <div className="bg-purple-100 px-4 py-2 rounded-full">
            <span className="font-medium">Photos Taken: </span>
            <span className="font-bold text-purple-600">{capturedPhotos.length}</span>
          </div>
          <div className="bg-blue-100 px-4 py-2 rounded-full">
            <span className="font-medium">Filters: </span>
            <span className="font-bold text-blue-600">{filters.length}</span>
          </div>
        </div>
      </div>

      {/* Camera Section */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl mb-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Camera View */}
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-slate-800">Camera</h4>
            
            <div className="relative bg-slate-100 rounded-xl overflow-hidden aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover ${isStreaming ? 'block' : 'hidden'}`}
                style={{ filter: filters.find(f => f.id === selectedFilter)?.style || 'none' }}
              />
              
              {isLoading && (
                <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center text-slate-600">
                  <div className="animate-spin text-4xl mb-2">⏳</div>
                  <div className="text-sm">Starting camera...</div>
                </div>
              )}
              
              {cameraError && (
                <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center text-red-600 p-4">
                  <div className="text-4xl mb-2">❌</div>
                  <div className="text-sm text-center">{cameraError}</div>
                  <button
                    onClick={() => setCameraError(null)}
                    className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-xs hover:bg-red-200 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}
              
              {!isStreaming && !isLoading && !cameraError && (
                <div className="absolute inset-0 w-full h-full flex items-center justify-center text-6xl text-slate-400">
                  📷
                </div>
              )}
              
              {/* Hidden canvas for capture */}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <div className="flex gap-3">
              {!isStreaming ? (
                <motion.button
                  onClick={startCamera}
                  disabled={isLoading}
                  whileHover={!isLoading ? { scale: 1.05 } : {}}
                  whileTap={!isLoading ? { scale: 0.95 } : {}}
                  className={`flex-1 py-3 px-6 rounded-lg font-bold shadow-lg transition-all ${
                    isLoading 
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-xl'
                  }`}
                >
                  {isLoading ? '⏳ Starting...' : '📹 Start Camera'}
                </motion.button>
              ) : (
                <>
                  <motion.button
                    id="capture-btn"
                    onClick={capturePhoto}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white py-3 px-6 rounded-lg font-bold shadow-lg"
                  >
                    📸 Capture Photo
                  </motion.button>
                  <motion.button
                    onClick={stopCamera}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-slate-200 text-slate-700 py-3 px-6 rounded-lg font-bold"
                  >
                    ⏹️ Stop
                  </motion.button>
                </>
              )}
            </div>
          </div>

          {/* Filter Selection */}
          <div className="space-y-4">
            <h4 className="text-xl font-bold text-slate-800">Filters</h4>
            
            <div className="grid grid-cols-3 gap-3 max-h-96 overflow-y-auto pr-2">
              {filters.map((filter) => (
                <motion.button
                  key={filter.id}
                  onClick={() => setSelectedFilter(filter.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    selectedFilter === filter.id
                      ? 'border-rose-500 bg-rose-50 shadow-lg'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="text-3xl mb-2">{filter.emoji}</div>
                  <div className="text-sm font-medium text-slate-700">{filter.name}</div>
                </motion.button>
              ))}
            </div>

            <div className="bg-slate-50 rounded-xl p-4">
              <h5 className="font-bold text-slate-800 mb-2">💡 Tips:</h5>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Make sure you have good lighting</li>
                <li>• Try different filters for fun effects</li>
                <li>• Photos are automatically saved</li>
                <li>• You can download your favorites</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Gallery */}
      {capturedPhotos.length > 0 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-xl font-bold text-slate-800">Your Photos ({capturedPhotos.length})</h4>
            <div className="flex gap-3">
              <motion.button
                onClick={() => setShowGallery(!showGallery)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium"
              >
                {showGallery ? 'Hide' : 'Show'} Gallery
              </motion.button>
              <motion.button
                onClick={clearAllPhotos}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-red-500 text-white px-4 py-2 rounded-lg font-medium"
              >
                Clear All
              </motion.button>
            </div>
          </div>

          {showGallery && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
              >
                {capturedPhotos.map((photo, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="relative group"
                  >
                    <img
                      src={photo}
                      alt={`Booth photo ${index + 1}`}
                      className="w-full aspect-square object-cover rounded-xl shadow-lg"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-center justify-center gap-2">
                      <motion.button
                        onClick={() => showPhotoPreview(photo)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="bg-blue-500 text-white px-3 py-2 rounded-lg font-medium shadow-lg text-sm"
                      >
                        👁️ Preview
                      </motion.button>
                      <motion.button
                        onClick={() => downloadPhoto(photo, index)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="bg-white text-slate-800 px-3 py-2 rounded-lg font-medium shadow-lg text-sm"
                      >
                        📥 Download
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
        </div>
      )}

      {/* Photo Preview Modal */}
      {showPreview && previewPhoto && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPreview(false)}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowPreview(false)}
              className="absolute top-4 right-4 z-10 bg-black/50 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              ✕
            </button>
            
            {/* Photo */}
            <img
              src={previewPhoto}
              alt="Photo preview"
              className="w-full h-auto max-h-[80vh] object-contain"
            />
            
            {/* Action buttons */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3">
              <motion.button
                onClick={downloadPreviewPhoto}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-rose-500 to-pink-600 text-white px-6 py-3 rounded-full font-bold shadow-lg flex items-center gap-2"
              >
                📥 Download Photo
              </motion.button>
              <motion.button
                onClick={() => setShowPreview(false)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-slate-200 text-slate-700 px-6 py-3 rounded-full font-bold shadow-lg"
              >
                Close
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Success Notification */}
      {showSuccess && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl z-50 flex items-center gap-3"
        >
          <div className="text-2xl">📸</div>
          <div>
            <div className="font-bold">Photo Captured!</div>
            <div className="text-sm opacity-90">Check your gallery or preview</div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default VirtualPhotoBooth;
